import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, optional, str, unknownValue, withDefault, type FieldParser, type Received } from 'validation';

const positionSchema: FieldParser<{ x: number | null; y: number | null }> = object<{ x: number | null; y: number | null }>({
  x: nullable(num()),
  y: nullable(num()),
});
import { handleSchema, type Handle } from 'api/node_definitions/endpoints/types';
import {
  nodeInstanceChildEdgeSchema,
  nodeInstanceRowCoreFields,
  statusUpdateSchema,
  type NodeInstanceChildEdge,
  type NodeInstanceRowCore,
  type StatusUpdate,
} from './types';
import { readResponse } from 'api/shared/readResponse';

// Loader for `GET /node_instances_flat/:id`, which feeds the InstanceViewer.
// Wire shapes re-derived from nap-backend `getNodeInstances.js`
// `getFlattenedParentNodeInstance`: root SELECT ~1416-1654, flattened children
// SELECT ~1782-1905, edges ~2022-2049. Unlike the subtree endpoint, children
// come back as a single FLAT array (no nesting); the graph is reconstructed
// from `child_edges`. See `nodeInstanceWire.ts` for conventions.

/** The focused instance in the flattened view (`NodeInstanceRowCore` + execution details). */
export interface FlatNodeInstance extends NodeInstanceRowCore {
  current_definition_version: number | null;
  definition_changed_after_execution: boolean;
  executed_definition_config: unknown;
  current_definition_config: unknown;
  status_updates: readonly StatusUpdate[] | null;
  output: unknown;
  outputdetails: unknown;
  files: unknown;
  exit_code: unknown;
  error: unknown;
  worker_pid: number | null;
  pid_start_time: string | null;
  activated_handles: unknown;
  parameters: unknown;
  file_event_zone_name: string | null;
  event_name: string | null;
  // Added by the route after the query.
  can_get_directory: boolean;
  executed_definition_snapshot?: unknown;
  current_definition_snapshot?: unknown;
}

const flatNodeInstanceSchema: FieldParser<FlatNodeInstance> = object<FlatNodeInstance>({
  ...nodeInstanceRowCoreFields,
  current_definition_version: nullable(num()),
  definition_changed_after_execution: bool(),
  executed_definition_config: unknownValue(),
  current_definition_config: unknownValue(),
  status_updates: nullable(array(statusUpdateSchema)),
  output: unknownValue(),
  outputdetails: unknownValue(),
  files: unknownValue(),
  exit_code: unknownValue(),
  error: unknownValue(),
  worker_pid: nullable(num()),
  pid_start_time: nullable(str()),
  activated_handles: unknownValue(),
  parameters: unknownValue(),
  file_event_zone_name: nullable(str()),
  event_name: nullable(str()),
  can_get_directory: bool(),
  executed_definition_snapshot: optional(unknownValue()),
  current_definition_snapshot: optional(unknownValue()),
});

/** One flattened descendant node. Carries flow-layout fields; no queue/worker joins, no nesting. */
export interface FlatChildNode {
  node_instance_id: number;
  parent_node_instance_id: number | null;
  node_id: string | null;
  // `node_instances.activated_handles` (uuid[], default '{}'): the ids of the handles the instance took.
  activated_handles: readonly string[] | null;
  iteration: number | null;
  definition_id: number | null;
  definition_version: number | null;
  status: string | null;
  sub_status: string | null;
  scheduled_start_datetime: string | null;
  scheduled_start_timezone: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  runtime_limit_min: number | null;
  runtime_limit_max: number | null;
  duration: number | null;
  created_at: string | null;
  name: string | null;
  description: string | null;
  // The definition's `node_definitions.handles`, LEFT JOINed.
  handles: readonly Handle[] | null;
  zone_name: string | null;
  type: string | null;
  sub_type: string | null;
  flow_parent_instance_id: number | null;
  label: string | null;
  spawned_node_instance_id: number | null;
  originating_node_instance_id: number | null;
  submit_and_forget: boolean;
  lock_name: string[] | null;
  has_child_nodes: boolean;
  has_skippable_wait_events: boolean;
  position: { x: number | null; y: number | null };
  parameters: unknown;
  file_event_instance_id: number | null;
  file_event_zone_name: string | null;
  event_name: string | null;
  process_event_name: string | null;
  scheduled_by: string | null;
}

const flatChildNodeSchema: FieldParser<FlatChildNode> = object<FlatChildNode>({
  node_instance_id: num(REJECT),
  parent_node_instance_id: nullable(num(REJECT)),
  node_id: nullable(str()),
  activated_handles: nullable(array(str())),
  iteration: nullable(num()),
  definition_id: nullable(num()),
  definition_version: nullable(num()),
  status: nullable(str()),
  sub_status: nullable(str()),
  scheduled_start_datetime: nullable(str()),
  scheduled_start_timezone: nullable(str()),
  start_datetime: nullable(str()),
  end_datetime: nullable(str()),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  duration: nullable(num()),
  created_at: nullable(str()),
  name: nullable(str()),
  description: nullable(str()),
  handles: nullable(array(handleSchema)),
  zone_name: nullable(str()),
  type: nullable(str()),
  sub_type: nullable(str()),
  flow_parent_instance_id: nullable(num()),
  label: nullable(str()),
  spawned_node_instance_id: nullable(num()),
  originating_node_instance_id: nullable(num()),
  submit_and_forget: bool(),
  lock_name: nullable(array(str())),
  has_child_nodes: bool(),
  has_skippable_wait_events: bool(),
  position: withDefault(positionSchema, { x: null, y: null }),
  parameters: unknownValue(),
  file_event_instance_id: nullable(num()),
  file_event_zone_name: nullable(str()),
  event_name: nullable(str()),
  process_event_name: nullable(str()),
  scheduled_by: nullable(str()),
});

export interface FlatNodeInstanceResponse {
  node_instance: FlatNodeInstance | null;
  child_nodes: FlatChildNode[];
  child_edges: NodeInstanceChildEdge[];
}

const flatResponseSchema: FieldParser<FlatNodeInstanceResponse> = object<FlatNodeInstanceResponse>({
  node_instance: nullable(flatNodeInstanceSchema),
  child_nodes: array(flatChildNodeSchema),
  child_edges: array(nodeInstanceChildEdgeSchema),
});

/**
 * Loads a node instance flattened with its descendants and flow edges. Throws
 * on a structurally malformed envelope; a missing focused row (`node_instance:
 * null`) is tolerated, and structurally broken children/edges are dropped.
 */
export const getFlatNodeInstance = async (id: number, source = 'getFlatNodeInstance'): Promise<Received<FlatNodeInstanceResponse>> => {
  const res = await api.get(`/node_instances_flat/${id}`);
  // The focused row may legitimately be null (missing). A present-but-unreadable
  // one ALSO arrives as null — `nullable` recovers it — but records the
  // substitution, so the two are told apart on the arm and not in the value.
  // The caller now gets the arm, so it CAN tell them apart — every caller still
  // collapses with `acceptDegraded` today, which is why the viewer reports an unreadable
  // row as "no such instance". That is now a call-site decision, not a limit here.
  return readResponse(res.data, { schema: flatResponseSchema, source });
};
